import { useCallback } from 'react';
import { useEpisodeStore } from '@/state/episodeStore';
import { useGenerationStore } from '@/state/generationStore';
import { generateSceneImage } from '@/services/scenesApi';
import { runMutation } from '@/state/saveQueue';
import { toast } from '@/state/toastStore';

/**
 * Sequential image-only generation of empty scenes in Scene.order asc. Stop
 * lets in-flight finish; rate-limit halts hard with a toast. Per Decision #19.
 */
export function useAutoPlay() {
  const start = useCallback(async (episodeUuid: string) => {
    const store = useGenerationStore.getState();
    if (store.autoPlayState !== 'idle') return;

    const empties = computeEmpties();
    if (empties.length === 0) return;

    store.startAutoPlay(empties.length);

    let succeeded = 0;
    let failed = 0;

    for (const scene of empties) {
      const current = useGenerationStore.getState().autoPlayState;
      if (current !== 'running') break;

      useGenerationStore.getState().setCurrent(scene.id);
      useGenerationStore.getState().markInFlight(scene.id);

      // Mark the scene optimistically as generating
      const before = scene.status;
      useEpisodeStore.getState().upsertScene({ ...scene, status: 'generating' });

      try {
        const image = await runMutation(
          {
            episodeUuid,
            endpoint: `/v2/scenes/${scene.id}/images`,
            method: 'POST',
            body: { prompt: scene.prompt, references: scene.references, fresh: false },
          },
          () =>
            generateSceneImage(episodeUuid, scene.id, {
              prompt: scene.prompt,
              references: scene.references,
              fresh: false,
            }),
        );
        useEpisodeStore.getState().upsertImage(image);
        useEpisodeStore.getState().upsertScene({
          ...scene,
          imageIds: [...scene.imageIds, image.id],
          status: 'ready',
          version: scene.version + 1,
          updatedAt: Date.now(),
        });
        succeeded += 1;
        useGenerationStore.getState().incrementCompleted();
      } catch (err) {
        if (isRateLimit(err)) {
          // Hard stop on rate limit per Decision #19
          useEpisodeStore.getState().upsertScene({ ...scene, status: before });
          useGenerationStore.getState().clearInFlight(scene.id);
          useGenerationStore.getState().stopAutoPlay();
          toast.err(
            'Auto-play stopped — Vertex rate limit hit. Try again in a minute.',
          );
          return;
        }
        useEpisodeStore.getState().upsertScene({ ...scene, status: 'error' });
        failed += 1;
      } finally {
        useGenerationStore.getState().clearInFlight(scene.id);
      }
    }

    useGenerationStore.getState().stopAutoPlay();
    if (failed === 0) {
      toast.ok(`Auto-play finished — ${succeeded} scenes rendered.`);
    } else {
      toast.warn(`Auto-play finished — ${succeeded} succeeded, ${failed} failed.`);
    }
  }, []);

  const stop = useCallback(() => {
    const state = useGenerationStore.getState();
    if (state.autoPlayState === 'running') {
      state.pauseAutoPlay();
      toast.info('Stopping after current scene…');
    } else {
      state.stopAutoPlay();
    }
  }, []);

  return { start, stop };
}

function computeEmpties() {
  const { scenes } = useEpisodeStore.getState();
  return scenes
    .filter((s) => !s.deletedAt && s.status === 'empty')
    .sort((a, b) => a.order - b.order);
}

function isRateLimit(err: unknown): boolean {
  if (err && typeof err === 'object' && 'status' in err) {
    return (err as { status: number }).status === 429;
  }
  if (err instanceof Error) {
    return /429|rate.?limit|RESOURCE_EXHAUSTED/i.test(err.message);
  }
  return false;
}
